import debug from "debug";
const log = debug("app:pwr");

/**
 * Manages the power data for objects in a 3D scene, aggregating and updating power-related information.
 * @category Components
 * @subcategory PowerTable
 */
export class PowerTableData {
  /**
   * Initializes a new instance of the PowerTableData class.
   * @param {Object} experience - The experience object containing the scene and event hub.
   * @param {THREE.Scene} experience.scene - The 3D scene containing the objects.
   * @param {Object} experience.eventHub - The event hub for managing application-level events.
   */
  constructor(experience) {
    /** @param {THREE.Scene} The 3D scene containing objects. */
    this.scene = experience.scene;

    /** @param {Object} The event hub for managing application-level events. */
    this.eventHub = experience.eventHub;

    /** @param {Map} Stores power data for objects in the scene. */
    this.powerObjectsMap = new Map();

    // Bind methods
    this.updatePowerData = this.updatePowerData.bind(this);

    // Set up event listeners
    this.setupEventListeners();
  }

  /**
   * Sets up event listeners for handling updates to power data.
   */
  setupEventListeners() {
    this.eventHub.on("xmd", this.updatePowerData);
  }

  cleanup() {
    console.log("PowerTableData cleanup started...");
  
    // 1. Remove event listeners from eventHub
    if (this.eventHub) {
      this.eventHub.off("xmd", this.updatePowerData);
    }
  
    // 2. Remove DOM event listeners
    document.removeEventListener("powerDataChanged", this.updatePowerData);
  
    // 3. Clear the powerObjectsMap
    if (this.powerObjectsMap) {
      this.powerObjectsMap.clear();
    }
  
    // 4. Clear object references
    this.scene = null;
    this.eventHub = null;
    this.powerObjectsMap = null;
  
    console.log("PowerTableData cleanup completed.");
  }

  /**
   * Updates the power data by clearing old data, filtering valid objects, aggregating their data,
   * populating the map, and notifying listeners about changes.
   */
  async updatePowerData() {
    this.powerObjectsMap.clear(); // Clear old data
    const filteredObjects = this.getFilteredObjects(); // Step 1: Filter valid objects
    const aggregatedObjects = this.aggregateObjects(filteredObjects); // Step 2: Aggregate data
    this.populatePowerObjectsMap(aggregatedObjects); // Step 3: Populate map with aggregated data
    this.notifyIfDataChanged(); // Notify about the changes
  }

  // Collect all valid objects into an array

  /**
   * Collects all valid objects from the scene that meet the power calculation criteria.
   * @returns {Array<Object>} An array of objects containing partNumber, volts, and current.
   */
  getFilteredObjects() {
    const filteredObjects = [];
    this.scene.traverse((object) => {
      if (object.isObject3D && object.userData.pn && object.name === "XMO") {
        const { pn: partNumber, properties } = object.userData;
        const volts = parseFloat(properties?.Vdd) || null;
        const current = parseFloat(properties?.IddD) || null;

        if (partNumber && volts && current) {
          filteredObjects.push({ partNumber, volts, current });
        }
      }
    });
    return filteredObjects;
  }

  // Aggregate objects by unique key

  /**
   * Aggregates the filtered objects by a unique key (part number, volts, and current).
   * @param {Array<Object>} filteredObjects - The filtered objects to aggregate.
   * @returns {Map<string, Object>} A map of aggregated objects with their quantities.
   */
  aggregateObjects(filteredObjects) {
    const aggregated = new Map();

    filteredObjects.forEach(({ partNumber, volts, current }) => {
      const key = `${partNumber}-${volts}-${current}`;
      if (!aggregated.has(key)) {
        aggregated.set(key, { partNumber, volts, current, qty: 1 });
      } else {
        aggregated.get(key).qty++;
      }
    });

    return aggregated;
  }

  // Populate the map with aggregated data

  /**
   * Populates the powerObjectsMap with aggregated power data.
   * @param {Map<string, Object>} aggregatedObjects - The aggregated data to populate the map.
   */
  populatePowerObjectsMap(aggregatedObjects) {
    let idCounter = 1;

    aggregatedObjects.forEach(({ partNumber, volts, current, qty }) => {
      const power = volts * current;
      const totalPower = power * qty;

      this.powerObjectsMap.set(`${partNumber}-${idCounter}`, {
        id: idCounter++,
        partNumber,
        qty,
        volts,
        current,
        power,
        totalPower,
      });
    });
  }

  /**
   * Notifies listeners about changes in power data by emitting events.
   */
  notifyIfDataChanged() {
    log("this is updated Power Table", this.getPowerTableData());
    this.eventHub.emit("powerUpdated", this.getPowerTableData());
    const event = new CustomEvent("powerDataChanged", {
      detail: this.getPowerTableData(),
    });
    document.dispatchEvent(event);
  }

  /**
   * Retrieves the current power table data, including headers, data rows, and totals.
   * @returns {Object} An object containing the headers, data rows, and totals.
   */
  getPowerTableData() {
    const header = {
      "Part Number": "partNumber",
      QTY: "qty",
      "Volts (V)": "volts",
      "Current (mA)": "current",
      "Pwr (mW)": "power",
    };
    const data = Array.from(this.powerObjectsMap.values());
    const total = this.calculateTotals(data);
    return { header, data, total };
  }

  /**
   * Calculates the total power value for all rows in the data.
   * @param {Array<Object>} data - The data rows to calculate totals from.
   * @returns {Array<string|number>} An array with the total power label and value.
   */
  calculateTotals(data) {
    const totalValue = data.reduce((sum, item) => sum + item.totalPower, 0);
    return ["Total Power (mW)", totalValue];
  }

  /**
   * Retrieves the power parts from the current power table data.
   * @returns {Object} An object containing the data rows and total values for power parts.
   */
  getPowerParts() {
    const powerTableData = this.getPowerTableData();
    const powerParts = {
      data: powerTableData.data.map((part) => ({
        id: part.id,
        partNumber: part.partNumber,
        qty: part.qty,
        volts: part.volts,
        current: part.current,
        power: part.power,
        totalPower: part.totalPower,
      })),
      total: powerTableData.total,
    };
    return powerParts;
  }
}
